import React, { useState } from "react";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import HistoryIcon from "@mui/icons-material/History";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import MailOutlinedIcon from "@mui/icons-material/MailOutlined";
import SupportAgentOutlinedIcon from "@mui/icons-material/SupportAgentOutlined";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import type { BenefitsTeamMessage } from "./benefitsSummary.api";
import { downloadBenefitsSummaryPdf } from "./downloadBenefitsSummaryPdf";
import { BenefitsSummaryData } from "./benefitsSummary.types";
import InsuranceDetailsCard from "./components/InsuranceDetailsCard";
import MessageBenefitsTeamDialog from "./components/MessageBenefitsTeamDialog";
import PlanLimits from "./components/PlanLimits";
import PreviousSummariesDrawer from "./components/PreviousSummariesDrawer";
import ServiceCostTable from "./components/ServiceCostTable";

interface BenefitsSummaryProps {
  data: BenefitsSummaryData;
  onSendMessage: (message: BenefitsTeamMessage) => Promise<void>;
}

const BenefitsSummary = ({ data, onSendMessage }: BenefitsSummaryProps) => {
  const [previousOpen, setPreviousOpen] = useState(false);
  const [messageOpen, setMessageOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      await downloadBenefitsSummaryPdf(data);
    } catch (error) {
      console.error("Benefits summary PDF error:", error);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1200, mx: "auto" }}>
      <Stack spacing={3}>
        {/* Header */}
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", md: "flex-end" }}
          spacing={2}
        >
          <Box>
            <Typography variant="h5" component="h1">
              Benefits summary for {data.childName}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              What your insurance provider told us about your plan on{" "}
              {data.checkedOn}.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1.5} sx={{ flexShrink: 0 }}>
            {data.previousSummaries.length > 0 && (
              <Button
                variant="outlined"
                startIcon={<HistoryIcon />}
                onClick={() => setPreviousOpen(true)}
              >
                Previous summaries
              </Button>
            )}
            <Button
              variant="contained"
              startIcon={<FileDownloadOutlinedIcon />}
              onClick={handleDownload}
              disabled={downloading}
            >
              Download PDF
            </Button>
          </Stack>
        </Stack>

        <InsuranceDetailsCard
          insurance={data.insurance}
          checkedOn={data.checkedOn}
        />

        {data.outOfPocketMaxMetNote && (
          <Alert severity="success" sx={{ alignItems: "center" }}>
            {data.outOfPocketMaxMetNote}
          </Alert>
        )}

        {data.referralRequired && (
          <Alert severity="warning" sx={{ alignItems: "center" }}>
            <AlertTitle sx={{ mb: 0.25 }}>Referral needed</AlertTitle>
            Your plan requires a referral before we can schedule. A member of
            our team will contact you to walk you through the steps.
          </Alert>
        )}

        {/* Plan details */}
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" component="h2">
            Plan details
          </Typography>
          <Typography variant="body2" color="text.secondary">
            What your plan lists for each service.
          </Typography>

          <Stack
            direction="row"
            spacing={1.5}
            sx={{ bgcolor: "primary.light", borderRadius: 1, p: 2, mt: 2.5 }}
          >
            <InfoOutlinedIcon color="primary" sx={{ mt: "2px" }} />
            <Box>
              <Typography variant="subtitle2" color="primary.main">
                Your first few visits
              </Typography>
              <Typography variant="body2">
                Your first few visits are usually a medical intake meeting or
                NeuroEvaluation. Both are medical visits, and they may not be
                scheduled yet. Some children go straight to therapy, and these
                visits do not apply.
              </Typography>
            </Box>
          </Stack>

          <Box sx={{ mt: 2 }}>
            <ServiceCostTable services={data.services} />
          </Box>

          {data.sharedLimitNote && (
            <Alert severity="info" variant="outlined" sx={{ mt: 2 }}>
              {data.sharedLimitNote}
            </Alert>
          )}

          <Box sx={{ mt: 4 }}>
            <Typography
              variant="subtitle1"
              component="h3"
              sx={{ fontWeight: 500, mb: 1 }}
            >
              Deductible and out-of-pocket maximum
            </Typography>
            <PlanLimits data={data} />
          </Box>
        </Paper>

        {/* Guidance */}
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper sx={{ p: 3, height: "100%" }}>
              <Typography variant="h6" component="h2" gutterBottom>
                What comes next
              </Typography>
              <Typography variant="body2">
                After your first medical visit, your doctor may recommend
                services for your child, such as ABA, occupational therapy, or
                speech therapy. What you pay can be different for each service,
                as listed above. If a service needs pre-authorization from your
                insurance, Cortica handles that for you.
              </Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper sx={{ p: 3, height: "100%" }}>
              <Typography variant="h6" component="h2" gutterBottom>
                How to read this summary
              </Typography>
              <Typography variant="body2">
                This summary is not a quote, and it does not guarantee what any
                visit will cost or what your plan will pay. What you pay depends
                on your plan's terms for each service, which services your care
                team recommends, how many hours are authorized, and how your
                insurance provider processes each claim. For the exact cost of a
                specific service, check your plan documents or call the number
                on the back of your insurance card.
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        {/* Contact */}
        <Paper sx={{ p: 3 }}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            alignItems={{ xs: "flex-start", md: "center" }}
            spacing={2}
          >
            <Avatar sx={{ bgcolor: "primary.light", color: "primary.main" }}>
              <SupportAgentOutlinedIcon />
            </Avatar>
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                Questions about this summary?
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Message our benefits team or call {data.benefitsTeamPhone}.
              </Typography>
            </Box>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1.5}
              sx={{ flexShrink: 0, width: { xs: "100%", md: "auto" } }}
            >
              <Button
                variant="outlined"
                startIcon={<MailOutlinedIcon />}
                onClick={() => setMessageOpen(true)}
              >
                Message benefits team
              </Button>
            </Stack>
          </Stack>
        </Paper>

        <Typography variant="caption" color="text.secondary">
          Information as of {data.checkedAt}. Source: your insurance provider.
        </Typography>
      </Stack>

      <PreviousSummariesDrawer
        open={previousOpen}
        onClose={() => setPreviousOpen(false)}
        summaries={data.previousSummaries}
      />

      <MessageBenefitsTeamDialog
        open={messageOpen}
        onClose={() => setMessageOpen(false)}
        onSend={onSendMessage}
        childName={data.childName}
      />
    </Box>
  );
};

export default BenefitsSummary;
